const BASE = '/api'

// ── Auth token ────────────────────────────────────────────────────────────────
export function getToken() { return localStorage.getItem('auth_token') }
export function setToken(t) { localStorage.setItem('auth_token', t) }
export function removeToken() { localStorage.removeItem('auth_token') }

function headers(extra = {}) {
  const h = { 'Content-Type': 'application/json', ...extra }
  const t = getToken()
  if (t) h['Authorization'] = `Bearer ${t}`
  return h
}

async function req(method, path, body) {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: headers(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || 'Request failed')
  return data
}

async function upload(path, file, extraFields = {}) {
  const fd = new FormData()
  fd.append('file', file)
  for (const [k, v] of Object.entries(extraFields)) fd.append(k, v)
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}` },
    body: fd,
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || 'Upload failed')
  return data
}

// ── Auth ──────────────────────────────────────────────────────────────────────
export async function signIn(email, password) {
  const data = await req('POST', '/auth/login', { email, password })
  setToken(data.token)
  return { data: { session: { user: { id: data.user.id, email: data.user.email } } }, error: null }
}

export async function signOut() {
  removeToken()
}

export async function getSession() {
  const token = getToken()
  if (!token) return { data: { session: null } }
  try {
    const user = await req('GET', '/auth/me')
    if (!user) { removeToken(); return { data: { session: null } } }
    return { data: { session: { user: { id: user.id, email: '' } } } }
  } catch {
    removeToken()
    return { data: { session: null } }
  }
}

export async function getUserProfile(userId) {
  try {
    return await req('GET', '/auth/me')
  } catch { return null }
}

// ── Locations ─────────────────────────────────────────────────────────────────
export async function fetchLocations() {
  return req('GET', '/locations')
}
export async function addLocation(loc) {
  return req('POST', '/locations', loc)
}
export async function updateLocation(id, updates) {
  return req('PUT', `/locations/${id}`, updates)
}
export async function deleteLocation(id) {
  return req('DELETE', `/locations/${id}`)
}

// ── Candidates ────────────────────────────────────────────────────────────────
export async function fetchCandidates(workspace = 'main') {
  return req('GET', `/candidates?workspace=${workspace}`)
}
export async function fetchAgentCandidates(workspace = 'main') {
  return req('GET', `/agent-candidates?workspace=${workspace}`)
}
export async function fetchActiveCandidates(workspace = 'main') {
  return req('GET', `/candidates?archived=false&status=not_завершен&workspace=${workspace}`)
}
export async function fetchArchivedCandidates(workspace = 'main') {
  return req('GET', `/candidates?archived=true&workspace=${workspace}`)
}
export async function fetchCompletedCandidates(workspace = 'main') {
  return req('GET', `/candidates?moscow_completed=true&workspace=${workspace}`)
}
export async function fetchCandidateById(id) {
  return req('GET', `/candidates/${id}`)
}
export async function addCandidate(candidate) {
  return req('POST', '/candidates', candidate)
}
export async function updateCandidate(id, updates) {
  return req('PUT', `/candidates/${id}`, updates)
}
export async function deleteCandidate(id) {
  return req('DELETE', `/candidates/${id}`)
}

// ── File uploads ──────────────────────────────────────────────────────────────
export async function uploadPhoto(file, candidateId) {
  const data = await upload('/upload/photo', file)
  return data.url
}
export async function uploadRouteFile(file, candidateId, pointIndex) {
  return upload('/upload/route-file', file)
}
export async function uploadAgentFile(agentId, type, file) {
  return upload('/upload/agent-file', file)
}
export async function uploadMoscowFile(candidateId, taskId, file) {
  return upload('/upload/moscow-file', file)
}
export async function refreshMoscowFileUrl(filePath) {
  const data = await req('GET', `/upload/moscow-file/refresh?path=${encodeURIComponent(filePath)}`)
  return data.url
}

// ── Cities ────────────────────────────────────────────────────────────────────
export async function fetchCities() {
  return req('GET', '/cities')
}
export async function addCity(name, timezone) {
  return req('POST', '/cities', { name, timezone })
}
export async function updateCity(id, updates) {
  return req('PUT', `/cities/${id}`, updates)
}
export async function deleteCity(id) {
  return req('DELETE', `/cities/${id}`)
}

// ── Activity log ──────────────────────────────────────────────────────────────
export async function fetchActivityLog(workspace = 'main') {
  return req('GET', `/activity-log?workspace=${workspace}`)
}
export async function addActivityLog(message, operator, workspace = 'main') {
  return req('POST', '/activity-log', { message, operator, workspace })
}

// ── Expense categories ────────────────────────────────────────────────────────
export async function fetchExpenseCategories() {
  return req('GET', '/expense-categories')
}
export async function addExpenseCategory(name, icon) {
  return req('POST', '/expense-categories', { name, icon })
}
export async function deleteExpenseCategory(id) {
  return req('DELETE', `/expense-categories/${id}`)
}

// ── Agents ────────────────────────────────────────────────────────────────────
export async function fetchAgents() {
  const workspace = process.env.REACT_APP_WORKSPACE
  return req('GET', workspace ? `/agents?workspace=${workspace}` : '/agents')
}
export async function fetchAgentById(id) {
  return req('GET', `/agents/${id}`)
}
export async function addAgent(agent) {
  return req('POST', '/agents', agent)
}
export async function updateAgent(id, updates) {
  return req('PUT', `/agents/${id}`, updates)
}
export async function deleteAgent(id) {
  return req('DELETE', `/agents/${id}`)
}

// ── Tickets ───────────────────────────────────────────────────────────────────
export async function fetchTickets() {
  return req('GET', '/tickets')
}
export async function fetchTicketById(id) {
  return req('GET', `/tickets/${id}`)
}
export async function addTicket(ticket) {
  return req('POST', '/tickets', ticket)
}
export async function updateTicket(id, updates) {
  return req('PUT', `/tickets/${id}`, updates)
}
export async function deleteTicket(id) {
  return req('DELETE', `/tickets/${id}`)
}

// ── Moscow pipeline ───────────────────────────────────────────────────────────
export async function getMoscowTasks() {
  return req('GET', '/moscow/tasks')
}
export async function getMoscowPipeline(candidateId) {
  return req('GET', `/moscow/pipeline/${candidateId}`)
}
export async function initMoscowPipeline(candidateId) {
  return req('POST', `/moscow/pipeline/init/${candidateId}`)
}
export async function updateMoscowTask(candidateId, taskId, updates) {
  return req('PUT', `/moscow/pipeline/${candidateId}/${taskId}`, updates)
}
export async function getMoscowProgressBatch(candidateIds) {
  return req('POST', '/moscow/progress-batch', { candidateIds })
}

// ── User profiles ─────────────────────────────────────────────────────────────
export async function fetchUserProfiles() {
  return req('GET', '/user-profiles')
}
export async function updateUserProfile(id, updates) {
  return req('PUT', `/user-profiles/${id}`, updates)
}

// ── Templates ─────────────────────────────────────────────────────────────────
export async function fetchTemplates(folder = '') {
  return req('GET', `/templates?folder=${encodeURIComponent(folder)}`)
}
export async function createTemplateFolder(name, folder, uploaderName) {
  return req('POST', '/templates/folder', { name, folder, uploaderName })
}
export async function uploadTemplateFile(file, folder, uploaderName) {
  const fd = new FormData()
  fd.append('file', file)
  fd.append('folder', folder || '')
  fd.append('uploaderName', uploaderName || '')
  const res = await fetch(`${BASE}/templates/file`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}` },
    body: fd,
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || 'Upload failed')
  return data
}
export async function deleteTemplate(id, url) {
  return req('DELETE', `/templates/${id}`)
}

// ── Geo tracking ──────────────────────────────────────────────────────────────
export async function getOrCreateGeoToken(candidateId) {
  return req('GET', `/geo/token/${candidateId}`)
}
export async function updateCandidateLocation(token, lat, lng, accuracy) {
  return req('PUT', `/geo/location/${token}`, { lat, lng, accuracy })
}
export async function getCandidateLocation(candidateId) {
  return req('GET', `/geo/by-candidate/${candidateId}`)
}
export async function getLocationByToken(token) {
  return req('GET', `/geo/by-token/${token}`)
}
export async function getGeoToken(candidateId) {
  return req('GET', `/geo/token/${candidateId}`)
}
export async function getGeoHistory(candidateId) {
  return req('GET', `/geo/history/${candidateId}`)
}
export async function getGeoMessages(candidateId) {
  return req('GET', `/geo/messages/${candidateId}`)
}
export async function postGeoMessage(candidateId, message, sentBy) {
  return req('POST', '/geo/messages', { candidate_id: candidateId, message, sent_by: sentBy })
}

// ── Requests ──────────────────────────────────────────────────────────────────
export async function fetchRequests(workspace = 'main') {
  return req('GET', `/requests?workspace=${workspace}`)
}
export async function addRequest(data) {
  return req('POST', '/requests', data)
}
export async function createRequestWithNotify(data) {
  return req('POST', '/requests/create-with-notify', data)
}
export async function updateRequest(id, updates) {
  return req('PUT', `/requests/${id}`, updates)
}
export async function uploadRequestFile(requestId, file) {
  return upload(`/upload/request-file`, file, { requestId })
}
export async function uploadNoteFile(file) {
  return upload('/upload/route-file', file)
}

// ── Tasks ─────────────────────────────────────────────────────────────────────
export async function fetchTasks(my = false, workspace = 'main') {
  return req('GET', `/tasks?workspace=${workspace}${my ? '&my=true' : ''}`)
}
export async function fetchCandidateTasks(candidateId) {
  return req('GET', `/tasks/candidate/${candidateId}`)
}
export async function addTask(task) {
  return req('POST', '/tasks', task)
}
export async function updateTask(id, updates) {
  return req('PUT', `/tasks/${id}`, updates)
}
export async function deleteTask(id) {
  return req('DELETE', `/tasks/${id}`)
}

// ── Agent advances ────────────────────────────────────────────────────────────
export async function fetchAgentAdvances() {
  return req('GET', '/agent-advances')
}
export async function addAgentAdvance(data) {
  return req('POST', '/agent-advances', data)
}
export async function deleteAgentAdvance(id) {
  return req('DELETE', `/agent-advances/${id}`)
}

// ── WhatsApp ──────────────────────────────────────────────────────────────────
export async function fetchUserActionLog(limit = 200) {
  return req('GET', `/user-action-log?limit=${limit}`)
}

export async function fetchAgentCountries() {
  return req('GET', '/agent-countries')
}
export async function addAgentCountry(name) {
  return req('POST', '/agent-countries', { name })
}
export async function updateAgentCountry(id, stages, renames) {
  return req('PUT', `/agent-countries/${id}`, { stages, renames })
}
export async function markWaRead(candidateId) {
  return req('POST', `/whatsapp/read/${candidateId}`)
}
export async function fetchWhatsappChannels() {
  return req('GET', '/whatsapp/channels')
}
export async function fetchWhatsappHistory(phone, channelId) {
  const qs = channelId ? `?channel_id=${encodeURIComponent(channelId)}` : ''
  return req('GET', `/whatsapp/history/${encodeURIComponent(phone)}${qs}`)
}
export async function sendWhatsappMessage(phone, text, channelId) {
  return req('POST', '/whatsapp/send', { phone, text, channel_id: channelId || null })
}

export async function sendSpecvisa(candidate_id, embassy, operator_name) {
  return req('POST', '/specvisa/send', { candidate_id, embassy, operator_name })
}
export async function sendVisaIssued(candidateId) {
  return req('POST', `/candidates/${candidateId}/visa-issued`, {})
}
export async function sendVisaReceived(candidateId, photo_url) {
  return req('POST', `/candidates/${candidateId}/visa-received`, { photo_url })
}

// ── Realtime (polling) ────────────────────────────────────────────────────────
// Заменяет subscribeToChanges из supabase.js
export function subscribeToChanges(onCandidateChange, onLocationChange) {
  let lastCheck = new Date().toISOString()
  let stopped = false

  async function poll() {
    if (stopped) return
    try {
      const data = await req('GET', `/poll/changes?since=${encodeURIComponent(lastCheck)}`)
      lastCheck = data.serverTime
      if (data.candidates?.length > 0) onCandidateChange({ eventType: 'UPDATE', updated: data.candidates })
      if (data.locations?.length > 0) onLocationChange({ eventType: 'UPDATE' })
    } catch { /* ignore poll errors */ }
    if (!stopped) setTimeout(poll, 5000)
  }

  setTimeout(poll, 5000)

  return {
    unsubscribe: () => { stopped = true },
  }
}

export function unsubscribe(channel) {
  if (channel?.unsubscribe) channel.unsubscribe()
}

// ── Совместимость с supabase клиентом для мест где используется напрямую ─────
export const supabase = {
  auth: {
    signInWithPassword: ({ email, password }) => signIn(email, password),
    signOut: () => { signOut(); return Promise.resolve() },
    getSession,
    onAuthStateChange: (cb) => {
      // Вызываем один раз при инициализации
      getSession().then(({ data }) => cb('INITIAL_SESSION', data.session))
      return { data: { subscription: { unsubscribe: () => {} } } }
    },
  },
  from: (table) => ({
    select: () => ({ eq: () => ({ single: () => Promise.resolve({ data: null }) }) }),
  }),
  channel: (name) => ({
    on: () => ({ subscribe: () => ({}) }),
  }),
  removeChannel: () => {},
}
